import React from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import ChatBubble from '@cloudscape-design/chat-components/chat-bubble';
import {Box, ExpandableSection, Popover, SpaceBetween, StatusIndicator} from '@cloudscape-design/components';
import {Streamdown} from 'streamdown';
import './cassandra.css';

interface StepInfo {
    label: string;
    detail?: string;
    status: 'loading' | 'success' | 'error' | 'info';
}

function parseStep(raw: string): StepInfo {
    if (raw.startsWith('🔧 ')) return {label: raw.slice(2).trim(), detail: undefined, status: 'loading'};
    if (raw.startsWith('❌ ')) return {label: raw.slice(2).trim(), detail: undefined, status: 'error'};
    if (raw.startsWith('💭 ')) return {label: 'Reasoning', detail: raw.slice(2).trim(), status: 'info'};
    if (raw.startsWith('✅ ')) return {label: raw.slice(2).trim(), detail: undefined, status: 'success'};
    return {label: raw, detail: undefined, status: 'info'};
}

interface ChatMessageProps {
    role: 'user' | 'assistant';
    text: string;
    loading?: boolean;
    steps?: string[];
    userAlias?: string;
    isStreaming?: boolean;
    timestamp?: number;
    onArtifactClick?: (data: unknown) => void;
}

export default function ChatMessage({role, text, loading, steps, userAlias, isStreaming = false, timestamp}: ChatMessageProps) {
    const isUser = role === 'user';

    const timeStr = timestamp ? new Date(timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : undefined;

    const avatar = isUser ? (
        <Avatar ariaLabel="User" tooltipText="User" initials={userAlias?.slice(0, 2).toUpperCase()} />
    ) : (
        <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Assistant" tooltipText="Cassandra" loading={loading && !text} />
    );

    return (
        <ChatBubble
            ariaLabel={`${isUser ? 'User' : 'Assistant'} message`}
            type={isUser ? 'outgoing' : 'incoming'}
            avatar={avatar}
            showLoadingBar={loading && !text}
        >
            <SpaceBetween size="xxxs">
                {steps && steps.length > 0 && (
                    <ExpandableSection
                        headerText={`${steps.length} step${steps.length > 1 ? 's' : ''}`}
                        variant="footer"
                        defaultExpanded={isStreaming}
                    >
                        <SpaceBetween size="xxxs">
                            {steps.map((raw, i) => {
                                const step = parseStep(raw);
                                return (
                                    <StatusIndicator key={i} type={step.status}>
                                        {step.detail ? (
                                            <Popover
                                                fixedWidth
                                                size="large"
                                                content={
                                                    <Box variant="code" fontSize="body-s">
                                                        {step.detail}
                                                    </Box>
                                                }
                                            >
                                                {step.label}
                                            </Popover>
                                        ) : (
                                            step.label
                                        )}
                                    </StatusIndicator>
                                );
                            })}
                        </SpaceBetween>
                    </ExpandableSection>
                )}
                {loading && !text ? (
                    <StatusIndicator type="loading">Thinking...</StatusIndicator>
                ) : (
                    <div className="cassandra-streamdown-scope">
                        <Streamdown animated isAnimating={isStreaming}>
                            {text}
                        </Streamdown>
                    </div>
                )}
                {timeStr && <Box variant="small">{timeStr}</Box>}
            </SpaceBetween>
        </ChatBubble>
    );
}
